import { useCallback, useRef, useState } from 'react'

interface ElementSize {
  width: number
  height: number
}

const EMPTY_SIZE: ElementSize = { width: 0, height: 0 }

/**
 * Tracks an element's untransformed layout size (`offsetWidth`/`offsetHeight`), so ancestor scale
 * transforms and in-flight enter/exit transforms don't feed back into the measurement.
 */
export function useElementSize<T extends HTMLElement>(): [(node: T | null) => void, ElementSize] {
  const [size, setSize] = useState<ElementSize>(EMPTY_SIZE)
  const observerRef = useRef<ResizeObserver | null>(null)

  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    if (!node) {
      return
    }

    const measure = () => {
      setSize((prev) =>
        prev.width === node.offsetWidth && prev.height === node.offsetHeight
          ? prev
          : { width: node.offsetWidth, height: node.offsetHeight },
      )
    }
    measure()
    observerRef.current = new ResizeObserver(measure)
    observerRef.current.observe(node)
  }, [])

  return [ref, size]
}
